import cx from 'classnames';
import { FC, useMemo } from 'react';

import { useAgencyConstants } from '@core/contexts/ConfigContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { CompanyRoles } from '@shared/apis/companiesApi.types';
import { Badge, BADGE_THEME } from '@shared/components/badge';
import { Ellipsis } from '@shared/components/ellipsis';
import { CloseIcon } from '@shared/icons/close';
import { DragIcon } from '@shared/icons/drag';
import { MultiPropertyIcon } from '@shared/icons/multi-property';
import { MixedCompany } from '@shared/models/company/company';
import { InputSize } from '@shared/types/common/input';

import { styles } from './CompanyLookupCard.styles';

export interface CompanyLookupCardProps extends WithStyles<typeof styles> {
  disabled?: boolean;
  draggable?: boolean;
  inputSize?: InputSize;
  item: MixedCompany;
  investment?: boolean;
  showRole?: boolean;
  withContainer?: boolean;
  onRemove?: (user: MixedCompany) => void;
  onView?: (id: number) => void;
}

const CompanyLookupCardComponent: FC<CompanyLookupCardProps> = ({
  classes,
  disabled,
  draggable,
  inputSize = InputSize.mediumLegacy,
  investment = false,
  item,
  showRole = false,
  withContainer = true,
  onRemove,
  onView,
}) => {
  const { constants } = useAgencyConstants();

  const isExisting = 'id' in item;
  const role = 'role' in item ? item.role : null;
  const roleVisible = role !== null && showRole;

  const fullAddress = useMemo(() => {
    if (!isExisting) {
      return '';
    }

    return [item.address1, item.town, item.postcode].filter(Boolean).join(', ');
  }, [item]);

  const roleText = useMemo(() => {
    if (!role) {
      return '';
    }

    if (investment && role === CompanyRoles.AcquisitionTenant) {
      return t('investor');
    }

    if (constants.companyRoles[role]) {
      return constants.companyRoles[role];
    }

    return t('unknown');
  }, [constants, investment, role, roleVisible]);

  return (
    <div
      className={cx(classes.root, classes[inputSize], { [classes.rootWithContainer]: withContainer })}
      key={isExisting ? item.id : item.name}
    >
      <div
        className={cx(classes.content, { [classes.contentClickable]: isExisting && onView })}
        onClick={isExisting && onView ? () => onView(item.id) : undefined}
      >
        {!disabled && draggable && <DragIcon classes={{ root: classes.dragIcon }} />}

        {isExisting ? (
          <div className={classes.buildingIconContainer}>
            <MultiPropertyIcon classes={{ root: classes.buildingIcon }} />
          </div>
        ) : (
          <Badge text={t('new')} theme={BADGE_THEME.green} classes={{ badge: classes.newBadge }} />
        )}

        <div className={classes.textContainer}>
          <Ellipsis classes={{ root: classes.text }} maxWidth="100%" text={item.name} withTooltip={false} />
          {roleVisible && (
            <Ellipsis classes={{ root: classes.subText }} maxWidth="100%" text={roleText} withTooltip={false} />
          )}
          {!roleVisible && fullAddress && (
            <Ellipsis classes={{ root: classes.subText }} maxWidth="100%" text={fullAddress} />
          )}
        </div>

        {!disabled && onRemove && (
          <div
            className={classes.closeIconContainer}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item);
            }}
          >
            <CloseIcon classes={{ root: classes.closeIcon }} />
          </div>
        )}
      </div>
    </div>
  );
};

export const CompanyLookupCard = withStyles(styles)(CompanyLookupCardComponent);
